/**
 * Nudgebox design tokens.
 *
 * Visual language: cool paper neutrals, a single blue accent, monospace
 * metadata, and *raised surfaces* — a dotted face resting on a hatched,
 * offset slab (see `components/surface.styles.ts`).
 *
 * Product code uses *semantic* tokens (`color.textSecondary`), never raw
 * values. Dark mode is a set of `stylex.createTheme` overrides (see
 * `themes.ts`) applied at the document root — no component needs to change.
 */
import * as stylex from "@stylexjs/stylex";
export const color = {
  bgPrimary: "var(--x1r4x44b)",
  bgSecondary: "var(--x135dim4)",
  bgTertiary: "var(--xr651mz)",
  bgHover: "var(--xevhi9x)",
  bgActive: "var(--x1ac7zqk)",
  bgInverse: "var(--x10k2y2e)",
  bgOverlay: "var(--x12l0zyj)",
  textPrimary: "var(--xyqwlu1)",
  textSecondary: "var(--xxo8iev)",
  textTertiary: "var(--xuwfo4h)",
  textDisabled: "var(--x10swv6z)",
  textInverse: "var(--x7k7cje)",
  textOnAccent: "var(--xwusvx8)",
  borderSubtle: "var(--x80z2tj)",
  borderDefault: "var(--x1kbr9ei)",
  borderStrong: "var(--xz0wm4t)",
  accentPrimary: "var(--xhmis13)",
  accentHover: "var(--x35ta1y)",
  accentSubtle: "var(--xljqlkv)",
  accentSoft: "var(--xd1vgi1)",
  accentText: "var(--x1ny8b65)",
  accentSlabBg: "var(--xtgadxt)",
  focusRing: "var(--xyufsx3)",
  slabBg: "var(--x1kdnkgc)",
  slabBorder: "var(--x193swgw)",
  slabInverseBg: "var(--x1295h3g)",
  dot: "var(--xgsx8ek)",
  dotInverse: "var(--xz2dvqs)",
  shimmer: "var(--xcm1wwe)",
  statusSuccess: "var(--xq45jjk)",
  statusSuccessSubtle: "var(--xgpny3i)",
  statusSuccessSoft: "var(--x1vctv30)",
  statusSuccessText: "var(--xuan7ax)",
  statusSuccessBorder: "var(--x11dje23)",
  statusWarning: "var(--x18peui8)",
  statusWarningSubtle: "var(--x1o1nej4)",
  statusWarningSoft: "var(--x12ef1b6)",
  statusWarningText: "var(--xipb5q5)",
  statusWarningBorder: "var(--x1gwcmv4)",
  statusError: "var(--x1g84don)",
  statusErrorSubtle: "var(--xy6ve2w)",
  statusErrorSoft: "var(--x1pewddq)",
  statusErrorText: "var(--x4mdl5h)",
  statusErrorBorder: "var(--x1nbgy8l)",
  statusNeutralSubtle: "var(--x17jf1k3)",
  codeBg: "var(--x1gwrxmw)",
  codeBorder: "var(--x133uuhr)",
  codeSlabBg: "var(--xjs3t71)",
  codeDivider: "var(--xg47y28)",
  codeText: "var(--x20tu3a)",
  codeMuted: "var(--xpk7fje)",
  __varGroupHash__: "xavfpcn"
};
export const space = {
  none: "var(--x1mvr3a1)",
  xxs: "var(--xd2pq6e)",
  xs: "var(--xp9i32i)",
  sm: "var(--x1o2luzl)",
  md: "var(--x1m1iprk)",
  lg: "var(--xfafkwq)",
  xl: "var(--x8qhfov)",
  xxl: "var(--x1rxnzmv)",
  x3: "var(--xuqik64)",
  x4: "var(--x1i79gve)",
  x5: "var(--xhm8ecd)",
  x6: "var(--x1ta5fz8)",
  __varGroupHash__: "xr0eh91"
};
export const radius = {
  sm: "var(--x7gfvnb)",
  md: "var(--x1pt26l)",
  lg: "var(--x1bwe067)",
  xl: "var(--x1efdghy)",
  full: "var(--x1y8gn69)",
  __varGroupHash__: "x1pxaomc"
};
export const font = {
  sans: "var(--x17yv81h)",
  mono: "var(--x1hunp9d)",
  __varGroupHash__: "xqzxg63"
};
export const fontSize = {
  xs: "var(--x1lyy73u)",
  sm: "var(--xser0er)",
  md: "var(--x16wfsd8)",
  lg: "var(--x1cd4uda)",
  xl: "var(--xvppq9n)",
  xxl: "var(--x1y3t4tz)",
  display: "var(--xivd8mb)",
  __varGroupHash__: "x1a476c0"
};
export const fontWeight = {
  regular: "var(--xvhmpa1)",
  medium: "var(--xcv3rek)",
  semibold: "var(--x1tt6swg)",
  __varGroupHash__: "xvljltv"
};
export const lineHeight = {
  tight: "var(--x1y05upz)",
  snug: "var(--x1eiybzf)",
  normal: "var(--x9rpczm)",
  relaxed: "var(--xk3hhoa)",
  __varGroupHash__: "x7oyhzs"
};
export const border = {
  hairline: "var(--x9d9ug7)",
  thick: "var(--xx2rnj2)",
  __varGroupHash__: "xxow7zu"
};
export const shadow = {
  xs: "var(--x1gi3z4t)",
  sm: "var(--xfmqqtk)",
  md: "var(--x1tdok6w)",
  lg: "var(--xurmlwl)",
  inset: "var(--x17yl6cq)",
  knob: "var(--xvdfyio)",
  __varGroupHash__: "xubp3va"
};

/**
 * Background-image recipes for the "paper" look: a fine dot grid on raised
 * faces and diagonal hatching on the offset slab behind them.
 */
export const texture = {
  dotSize: "var(--xozrvhx)",
  hatch: "var(--x1ktgpqq)",
  hatchInverse: "var(--x1n2pppp)",
  __varGroupHash__: "x1lpt968"
};

/**
 * Every overlay portals to <body>, so these layers compete directly. Popups
 * (select, menu, popover) sit above modals because they open from inside them.
 */
export const zIndex = {
  base: "var(--xfrjm1z)",
  sticky: "var(--xqtm2wi)",
  overlay: "var(--x19u64y5)",
  modal: "var(--xmfrue5)",
  dropdown: "var(--x1evywn9)",
  toast: "var(--x1vsoklg)",
  tooltip: "var(--x1xe38h2)",
  __varGroupHash__: "x13ts9vf"
};
export const motion = {
  fast: "var(--xjs1vx7)",
  normal: "var(--x1m4hfgf)",
  easeOut: "var(--x1hpj3un)",
  __varGroupHash__: "xdjbdhh"
};

/**
 * Media-query keys for conditional styles:
 *
 *   height: { default: 34, [media.touch]: 44 }
 *
 * `mobile` switches layout (phone-sized viewports); `touch` sizes controls
 * for fingers on any coarse-pointer device, tablets included. Keep `mobile`
 * in sync with `MOBILE_QUERY` in `hooks/useMediaQuery.ts`.
 */
export const media = {
  mobile: "@media (max-width: 767px)",
  touch: "@media (pointer: coarse)"
};